import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  Heart, Brain, Wallet, BookOpen, Sparkles,
  ArrowRight, Check, Loader2
} from "lucide-react";
import ProfileSelection from "@/components/onboarding/ProfileSelection";

const CATEGORIES = [
  {
    id: "saude",
    label: "Saúde",
    description: "Exercícios, hidratação, sono",
    icon: Heart,
    color: "#F43F5E",
    bg: "rgba(244, 63, 94, 0.12)",
  },
  {
    id: "produtividade",
    label: "Produtividade",
    description: "Planejamento, foco, metas",
    icon: Brain,
    color: "#8B5CF6",
    bg: "rgba(139, 92, 246, 0.12)",
  },
  {
    id: "financas",
    label: "Finanças",
    description: "Gastos, orçamento, investimento",
    icon: Wallet,
    color: "#10B981",
    bg: "rgba(16, 185, 129, 0.12)",
  },
  {
    id: "aprendizado",
    label: "Aprendizado",
    description: "Leitura, cursos, idiomas",
    icon: BookOpen,
    color: "#F59E0B",
    bg: "rgba(245, 158, 11, 0.12)",
  },
  {
    id: "bem_estar",
    label: "Bem-estar",
    description: "Meditação, gratidão, mindfulness",
    icon: Sparkles,
    color: "#06B6D4",
    bg: "rgba(6, 182, 212, 0.12)",
  },
];

export default function OnboardingPage() {
  const { user, completeOnboarding, applyProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [finishMode, setFinishMode] = useState(null); // "profile" | "categories"

  const toggleCategory = useCallback((id) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  }, []);

  const handleComplete = useCallback(async () => {
    setLoading(true);
    try {
      const data = await completeOnboarding(selectedCategories);
      setResult(data);
      setFinishMode("categories");
      setStep(3);
      toast.success("Tudo pronto!");
    } catch (err) {
      toast.error("Erro ao configurar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [completeOnboarding, selectedCategories]);

  const handleSkip = useCallback(async () => {
    setLoading(true);
    try {
      await completeOnboarding([]);
      toast.success("Você pode configurar isso depois!");
    } catch {
      toast.error("Erro ao pular.");
    } finally {
      setLoading(false);
    }
  }, [completeOnboarding]);

  const handleProfileApplied = useCallback((data) => {
    setResult(data);
    setFinishMode("profile");
    setStep(3);
    toast.success(`Perfil "${data.profile_name}" aplicado!`);
  }, []);

  const firstName = user?.name?.split(" ")[0] || "Usuário";

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: "linear-gradient(135deg, #0B0F1A 0%, #121826 50%, #0D1220 100%)" }}
    >
      <div className="w-full max-w-lg">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-1.5 rounded-full transition-all duration-500"
              style={{
                width: step === i ? 32 : 8,
                background: step >= i ? "#6366F1" : "#1F2A44",
              }}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 0: Welcome */}
          {step === 0 && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <motion.div
                className="inline-flex items-center justify-center w-20 h-20 rounded-3xl mb-6"
                style={{ background: "linear-gradient(135deg, #6366F1, #8B5CF6)" }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <span className="text-3xl font-bold text-white">J</span>
              </motion.div>

              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                Olá, {firstName}!
              </h1>
              <p className="text-[#94A3B8] text-base mb-8 max-w-sm mx-auto leading-relaxed">
                Sou o Nexo, seu assistente pessoal. Vou te ajudar a organizar seu dia,
                acompanhar hábitos e muito mais.
              </p>

              <div
                className="rounded-2xl p-6 mb-8 text-left border"
                style={{ background: "rgba(99, 102, 241, 0.08)", borderColor: "rgba(99, 102, 241, 0.2)" }}
              >
                <p className="text-white text-sm font-medium mb-3">O que posso fazer por você:</p>
                <ul className="space-y-2">
                  {[
                    "Gerenciar tarefas e lembretes por voz ou texto",
                    "Acompanhar seus hábitos e streaks",
                    "Controlar suas finanças pessoais",
                    "Fazer anotações e organizar ideias",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#94A3B8] text-sm">
                      <Check className="w-4 h-4 text-[#6366F1] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                data-testid="onboarding-next-btn"
                onClick={() => setStep(1)}
                className="px-8 py-3 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 inline-flex items-center gap-2"
                style={{ background: "linear-gradient(135deg, #6366F1, #8B5CF6)" }}
              >
                Vamos começar
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* Step 1: Profile selection (ONBOARDING-PROFILES) */}
          {step === 1 && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <ProfileSelection
                onApply={applyProfile}
                onApplied={handleProfileApplied}
                onSkip={() => setStep(2)}
              />
            </motion.div>
          )}

          {/* Step 2: Preferences (legacy manual flow) */}
          {step === 2 && (
            <motion.div
              key="preferences"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">
                  No que posso te ajudar?
                </h2>
                <p className="text-[#94A3B8] text-sm">
                  Selecione as áreas e eu crio hábitos e tarefas iniciais pra você
                </p>
              </div>

              <div className="grid gap-3 mb-8">
                {CATEGORIES.map((cat) => {
                  const selected = selectedCategories.includes(cat.id);
                  const Icon = cat.icon;
                  return (
                    <motion.button
                      key={cat.id}
                      data-testid={`onboarding-category-${cat.id}`}
                      onClick={() => toggleCategory(cat.id)}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="flex items-center gap-4 p-4 rounded-xl border text-left transition-all"
                      style={{
                        background: selected ? cat.bg : "rgba(18, 24, 38, 0.6)",
                        borderColor: selected ? cat.color : "#1F2A44",
                      }}
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: cat.bg }}
                      >
                        <Icon className="w-5 h-5" style={{ color: cat.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{cat.label}</p>
                        <p className="text-[#64748B] text-xs">{cat.description}</p>
                      </div>
                      <div
                        className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
                        style={{
                          borderColor: selected ? cat.color : "#475569",
                          background: selected ? cat.color : "transparent",
                        }}
                      >
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <button
                  data-testid="onboarding-skip-btn"
                  onClick={handleSkip}
                  disabled={loading}
                  className="flex-1 py-3 rounded-xl text-[#94A3B8] text-sm font-medium border transition-all hover:text-white hover:border-[#475569]"
                  style={{ borderColor: "#1F2A44" }}
                >
                  Pular
                </button>
                <button
                  data-testid="onboarding-complete-btn"
                  onClick={handleComplete}
                  disabled={loading || selectedCategories.length === 0}
                  className="flex-1 py-3 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  style={{ background: "linear-gradient(135deg, #6366F1, #8B5CF6)" }}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Configurar ({selectedCategories.length})
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Done */}
          {step === 3 && result && (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <motion.div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                style={{ background: "rgba(16, 185, 129, 0.15)" }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
              >
                <Check className="w-8 h-8 text-[#10B981]" />
              </motion.div>

              <h2 className="text-2xl font-bold text-white mb-2">Tudo pronto!</h2>

              {finishMode === "profile" && result.items_created && (
                <>
                  <p className="text-[#94A3B8] text-sm mb-6">
                    Perfil <span className="text-white font-medium">"{result.profile_name}"</span> aplicado: {result.items_created.categories} categorias, {result.items_created.habits} hábitos, {result.items_created.reminders} lembretes, {result.items_created.projects} projetos.
                  </p>
                  <div
                    className="rounded-2xl p-5 mb-8 text-left border"
                    style={{ background: "rgba(18, 24, 38, 0.8)", borderColor: "#1F2A44" }}
                  >
                    <p className="text-[#94A3B8] text-xs font-medium mb-3 uppercase tracking-wider">
                      Resumo
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <p className="text-white"><span className="text-[#8B5CF6]">●</span> {result.items_created.categories} categorias</p>
                      <p className="text-white"><span className="text-[#6366F1]">●</span> {result.items_created.habits} hábitos</p>
                      <p className="text-white"><span className="text-[#06B6D4]">●</span> {result.items_created.reminders} lembretes</p>
                      <p className="text-white"><span className="text-[#10B981]">●</span> {result.items_created.projects} projetos</p>
                      <p className="text-white col-span-2"><span className="text-[#F59E0B]">●</span> {result.items_created.budget_goals} metas de orçamento sugeridas</p>
                    </div>
                  </div>
                </>
              )}

              {finishMode === "categories" && (
                <>
                  <p className="text-[#94A3B8] text-sm mb-6">
                    Criei {result.created_habits?.length || 0} hábitos e{" "}
                    {result.created_tasks?.length || 0} tarefas pra você começar
                  </p>

                  {(result.created_habits?.length > 0 || result.created_tasks?.length > 0) && (
                    <div
                      className="rounded-2xl p-5 mb-8 text-left border"
                      style={{ background: "rgba(18, 24, 38, 0.8)", borderColor: "#1F2A44" }}
                    >
                      {result.created_habits?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-[#94A3B8] text-xs font-medium mb-2 uppercase tracking-wider">
                            Hábitos criados
                          </p>
                          <div className="space-y-1">
                            {result.created_habits.map((h, i) => (
                              <p key={i} className="text-white text-sm flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#8B5CF6]" />
                                {h}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      {result.created_tasks?.length > 0 && (
                        <div>
                          <p className="text-[#94A3B8] text-xs font-medium mb-2 uppercase tracking-wider">
                            Tarefas criadas
                          </p>
                          <div className="space-y-1">
                            {result.created_tasks.map((t, i) => (
                              <p key={i} className="text-white text-sm flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#6366F1]" />
                                {t}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <p className="text-[#64748B] text-xs mb-6">
                Dica: diga <span className="text-[#8B5CF6] font-medium">"Nexo"</span> para me ativar por voz a qualquer momento
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
